import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChatSidebar from './ChatSidebar';
import ChatSearch from './ChatSearch';
import { getChatHistory, getChatSession, deleteChatSession, saveChatSession } from '../utils/api';

const CHAT_CACHE_KEY = 'leadgen_chat_sessions_cache';

const ChatLayout = ({ onSearchStart, onSearchComplete, onSearchError }) => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const [newChatTrigger, setNewChatTrigger] = useState(0);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(CHAT_CACHE_KEY);
      if (cached) {
        setSessions(JSON.parse(cached));
      }
    } catch (err) {
      console.error('Failed to parse cached sessions:', err);
    }
  }, []);

  const refreshSessions = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const cached = localStorage.getItem(CHAT_CACHE_KEY);
      if (cached) {
        try {
          setSessions(JSON.parse(cached));
        } catch (e) {}
      }

      const history = await getChatHistory();
      const freshData = history || [];
      setSessions(freshData);
      localStorage.setItem(CHAT_CACHE_KEY, JSON.stringify(freshData));
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
    }
  }, [profile?.id]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const handleNewChat = () => {
    setActiveSessionId(null);
    setSelectedSessionData(null);
    setNewChatTrigger(prev => prev + 1);
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const handleSelectSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    try {
      const fullSession = await getChatSession(sessionId);
      if (fullSession) {
        setSelectedSessionData(fullSession);
      }
    } catch (err) {
      console.error('Error loading chat session:', err);
    }
    if (window.innerWidth < 768) setSidebarOpen(false);
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteChatSession(sessionId);
      setSessions(prev => {
        const newSessions = prev.filter(s => s.id !== sessionId);
        localStorage.setItem(CHAT_CACHE_KEY, JSON.stringify(newSessions));
        return newSessions;
      });
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setSelectedSessionData(null);
      }
    } catch (err) {
      console.error('Error deleting chat session:', err);
    }
  };

  const handleRenameSession = async (sessionId, newTitle) => {
    // Optimistic local update
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: newTitle } : s));
    try {
      const fullSession = await getChatSession(sessionId);
      if (fullSession) {
        await saveChatSession({
          session_id: sessionId,
          title: newTitle,
          messages: fullSession.messages,
          search_params: fullSession.search_params,
          job_id: fullSession.job_id
        });
      }
    } catch (err) {
      console.error('Error renaming chat session:', err);
      // Revert optimism on error
      refreshSessions();
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] relative bg-zinc-950 text-white">
      <ChatSidebar 
        sessions={sessions}
        activeSessionId={activeSessionId}
        onNewChat={handleNewChat}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        onRenameSession={handleRenameSession}
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onNavigate={handleNavigate}
      />
      
      <div className="flex-1 min-w-0 overflow-hidden relative">
        <button
          onClick={toggleSidebar}
          className="md:hidden absolute top-4 left-4 z-30 p-2 text-gray-400 hover:text-white bg-zinc-800/80 backdrop-blur rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors"
          aria-label="Toggle Sidebar"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <ChatSearch 
          onSearchStart={onSearchStart}
          onSearchComplete={onSearchComplete}
          onSearchError={onSearchError}
          activeSessionId={activeSessionId}
          selectedSessionData={selectedSessionData}
          onNewChat={handleNewChat}
          onSessionSaved={refreshSessions}
          newChatTrigger={newChatTrigger}
        />
      </div>
    </div>
  );
};

export default ChatLayout;
